/**
 * What each role may do. The one table every access decision in the app reads —
 * the server (src/adapters/rpc/router.ts), the pages (routes.ts) and the menus.
 *
 * To add a permission: add it here with its roles and a sentence, then add it
 * to docs/PERMISSIONS.md. access.test.ts fails until the doc mentions it.
 */
import type { Role } from './roles.ts'

export const permissions = {
  'app.use': {
    roles: ['admin', 'staff'],
    description: 'Use the app: see and change records — projects, reports, messages.',
  },
  'users.view': {
    roles: ['admin', 'staff'],
    description: 'See who is in the workspace (for example, to pick someone).',
  },
  'settings.manage': {
    roles: ['admin'],
    description: 'Open Settings and change how the workspace works.',
  },
  'users.manage': {
    roles: ['admin'],
    description:
      'Invite people, change their role, deactivate or remove them, resend or withdraw invitations — and which email domains join automatically.',
  },
  'assistant.use': {
    roles: ['admin', 'staff'],
    description:
      'Chat with the AI assistant — which can only do what you can, and asks before every change.',
  },
  'users.impersonate': {
    roles: ['admin'],
    description: 'Use the app as a Staff member sees it, for up to an hour, on the record.',
  },
  'system.view': {
    roles: ['admin'],
    description: 'See the System page: the stack, services, usage and spend.',
  },
  'system.manage': {
    roles: ['admin'],
    description: 'Set service prices and ask the vendors for fresh usage.',
  },
  'mcp.use': {
    roles: ['admin', 'staff'],
    description:
      'Connect an AI app (Claude, ChatGPT…) through MCP, and make personal access tokens — it reads only what you can (docs/MCP.md).',
  },
  'records.purge': {
    roles: ['admin'],
    description:
      'Delete forever: destroy a client, job or invoice already in Recently deleted. Deleting, which can be undone, is app.use.',
  },
  'notes.changeAny': {
    roles: ['admin'],
    description:
      'Edit or delete anyone’s note on a client or location. Everyone else can edit and delete only the notes they wrote.',
  },
  'testMode.use': {
    roles: ['admin'],
    description:
      'Switch Test mode on to see golden records and unfinished features — never real data.',
  },
} as const satisfies Record<string, { roles: readonly Role[]; description: string }>

export type Permission = keyof typeof permissions
export const permissionIds = Object.keys(permissions) as Permission[]

export function can(role: Role, permission: Permission): boolean {
  return (permissions[permission].roles as readonly Role[]).includes(role)
}

/** Every permission a role has — for the Account page and the doc. */
export function permissionsOf(role: Role): Permission[] {
  return permissionIds.filter((permission) => can(role, permission))
}
